import { FilterRow } from "@components/FilterTag/FilterTag";
import { $staffApi } from "../../../api";
import { ThreadQuery } from "../types";
import { Dispatch, SetStateAction, useState } from "react";
import { useInstitutionContext } from "@hooks/institutionContext";
import { Checkbox, LoadingSkeleton } from "@narmi/design_system";

export function CustomerTag({
  filters,
  isExpanded,
  close,
  open,
  setFilters,
}: {
  filters: ThreadQuery;
  isExpanded: boolean;
  close: () => void;
  open: () => void;
  setFilters: Dispatch<SetStateAction<ThreadQuery>>;
}) {
  const { institution } = useInstitutionContext();
  const [searchValue, setSearchValue] = useState("");
  const [customerFilters, setCustomerFilters] = useState(filters.user);

  // Fetch users for dropdown based on search input
  const { data: usersSearchData, isLoading: isLoadingUsersSearch } = $staffApi.useQuery(
    "get",
    "/api/threads/users/",
    {
      params: {
        query: {
          search: searchValue,
          is_staff: false,
          limit: 10,
        },
      },
    }
  );

  // Fetch any users that are already selected by the filters, regardless of the search value
  const { data: queryUsersData, isLoading: isLoadingQueryUsers } = $staffApi.useQuery(
    "get",
    "/api/threads/users/",
    {
      params: {
        query: {
          id: filters.user,
          is_staff: false,
        },
      },
    },
    {
      enabled: filters.user.length > 0,
    }
  );
  const queryUsers = queryUsersData?.results || [];
  const queryUsersSet = new Set(queryUsers.map((user) => user.id));

  const filteredQueryUsers = queryUsers.filter(
    (user) =>
      user.full_name.trim() && user.full_name.toLowerCase().includes(searchValue.toLowerCase())
  );
  const filteredSearchUsers =
    usersSearchData?.results.filter((user) => !queryUsersSet.has(user.id)) || [];

  return (
    <FilterRow.Tag
      label={institution.institution_type === "bank" ? "Customer" : "Member"}
      countApplied={filters.user.length}
      isExpanded={isExpanded}
      onPopoverDismiss={() => {
        setSearchValue("");
        close();
      }}
      onToggle={() => open()}
    >
      <FilterRow.Search
        searchValue={searchValue}
        onDebouncedSearch={(debouncedValue: string) => setSearchValue(debouncedValue)}
      />
      <FilterRow.FormContainer>
        <LoadingSkeleton isLoading={isLoadingUsersSearch || isLoadingQueryUsers}>
          {filteredQueryUsers.map((user) => (
            <Checkbox
              key={user.id}
              label={user.full_name}
              checked={customerFilters.includes(user.id)}
              onChange={() => {
                const isChecked = customerFilters.includes(user.id);
                const newFilters = isChecked
                  ? customerFilters.filter((id) => id !== user.id)
                  : [...customerFilters, user.id];
                setCustomerFilters(newFilters);
              }}
            />
          ))}
          {filteredSearchUsers.map((user) => (
            <Checkbox
              key={user.id}
              label={user.full_name}
              checked={customerFilters.includes(user.id)}
              onChange={() => {
                const isChecked = customerFilters.includes(user.id);
                const newFilters = isChecked
                  ? customerFilters.filter((id) => id !== user.id)
                  : [...customerFilters, user.id];
                setCustomerFilters(newFilters);
              }}
            />
          ))}
          {!filteredQueryUsers.length && !filteredSearchUsers.length ? (
            <FilterRow.NoResultsFound />
          ) : null}
        </LoadingSkeleton>
      </FilterRow.FormContainer>
      <FilterRow.ActionBar
        onClear={() => {
          setSearchValue("");
          setCustomerFilters([]);
          setFilters((prev) => ({
            ...prev,
            user: [],
            offset: 0,
          }));
          close();
        }}
        onApply={() => {
          setSearchValue("");
          setFilters((prev) => ({
            ...prev,
            user: customerFilters,
            offset: 0,
          }));
          close();
        }}
      />
    </FilterRow.Tag>
  );
}
